import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Layout.css';
import SidebarCalendar from './SidebarCalendar';
import {
  CalendarIcon,
  CloseIcon,
  DashboardIcon,
  GateIcon,
  MapPinIcon,
  MenuIcon,
  PassIcon,
  ShieldIcon,
} from './Icons';

export default function Layout({ children }) {
  const { faculty, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = useMemo(() => {
    if (faculty?.role === 'ao') {
      return [{ path: '/ao', label: 'AO approvals', icon: ShieldIcon }];
    }
    const base = [
      { path: '/dashboard', label: 'Dashboard', icon: DashboardIcon },
      { path: '/events', label: 'My events', icon: CalendarIcon },
      { path: '/venues', label: 'Venues', icon: MapPinIcon },
      { path: '/calendar', label: 'Venue calendar', icon: CalendarIcon },
      { path: '/passes', label: 'Visitor passes', icon: PassIcon },
      { path: '/gate', label: 'Gate security', icon: GateIcon },
    ];
    return base;
  }, [faculty?.role]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = faculty?.name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() || 'FA';

  return (
    <div className={`layout ${collapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
      <header className="mobile-topbar">
        <button type="button" className="icon-btn mobile-menu-btn" onClick={() => setMobileMenuOpen(true)} aria-label="Open navigation">
          <MenuIcon />
        </button>
        <div className="mobile-brand">
          <div className="logo-mark">VP</div>
          <div>
            <div className="mobile-brand-title">VisitorPass</div>
            <div className="mobile-brand-subtitle">Campus access</div>
          </div>
        </div>
        <button type="button" className="icon-btn mobile-logout-btn" onClick={handleLogout} aria-label="Logout">
          <ShieldIcon />
        </button>
      </header>

      <div className={`sidebar-backdrop ${mobileMenuOpen ? 'show' : ''}`} onClick={() => setMobileMenuOpen(false)} role="presentation" />

      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <span className="logo-mark">VP</span>
            {!collapsed && <span className="logo-text">VisitorPass</span>}
          </div>
          <div className="sidebar-actions">
            <button type="button" className="collapse-btn desktop-only" onClick={() => setCollapsed((p) => !p)} aria-label="Toggle sidebar">
              {collapsed ? '›' : '‹'}
            </button>
            <button type="button" className="icon-btn mobile-only" onClick={() => setMobileMenuOpen(false)} aria-label="Close navigation">
              <CloseIcon />
            </button>
          </div>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink key={item.path} to={item.path} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <span className="nav-icon">
                <item.icon />
              </span>
              {!collapsed && <span className="nav-label">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <SidebarCalendar collapsed={collapsed} />

        <div className="sidebar-footer">
          <div className="faculty-avatar">{initials}</div>
          {!collapsed && (
            <div className="faculty-info">
              <div className="faculty-name">{faculty?.name}</div>
              <div className="faculty-dept">{faculty?.department}</div>
            </div>
          )}
          <button type="button" className="logout-btn" onClick={handleLogout} aria-label="Sign out" title="Sign out">
            {collapsed ? '↪' : 'Sign out'}
          </button>
        </div>
      </aside>

      <main className="main-content">{children}</main>
    </div>
  );
}
